<script lang="ts">
  import { Text, Media, Markdown, Button } from '@lib/components';
  import type { FeatureBlock } from './types';
  import { responsiveStyle, parseResponsiveCSS, navigate, slide } from '@lib/directives';
  import { handleCtaNavigation } from '@lib/utils/navigation';

  export let block: FeatureBlock;

  let slideDirection = 1;
  export let isFullscreen: boolean = false;
  export let onCtaClick: ((action?: string) => void) | undefined = undefined;

  let containerEl: HTMLElement;
  let drawerOpen = false;
  let contentVisible = false;
  let textVisible = true;
  let currentSlide = 0;
  let touchStartX = 0;
  let touchEndX = 0;

  $: layout = block.layout?.variant || 'media-right';

  $: isClickable = block.cta && (block.cta.action || block.cta.url || block.cta.href) && !block.cta.label;

  $: hasMedia = !!block.media;
  $: hasDrawer = !!block.drawer?.slides?.length;
  $: isDrawerAction = block.cta?.action === 'drawer';
  $: slides = block.drawer?.slides || [];
  $: totalSlides = slides.length;

  $: textColor = block.config?.textColor;
  $: textDarkColor = block.config?.textColorDark;

  $: drawerOffWidth = block.layout?.drawerOff ?? 800;
  $: drawerOnWidth = block.layout?.drawerOn ?? 1096;

  $: responsiveCSS = block.css ? parseResponsiveCSS(block.css) : null;

  function toggleDrawer() {
    if (drawerOpen) {
      // CLOSING sequence: fade out slider content > collapse layout > fade in text
      contentVisible = false;
      setTimeout(() => {
        drawerOpen = false;
        currentSlide = 0;
        // Wait for flex transition (250ms) before showing text
        setTimeout(() => {
          textVisible = true;
        }, 300);
      }, 200);
    } else {
      // OPENING sequence: fade out text > expand layout > fade in slider content
      textVisible = false;
      setTimeout(() => {
        drawerOpen = true;
        currentSlide = 0;
        setTimeout(() => {
          contentVisible = true;
        }, 200);
        if (containerEl) {
          setTimeout(() => {
            const rect = containerEl.getBoundingClientRect();
            const scrollTop = window.pageYOffset + rect.top - 80;
            window.scrollTo({ top: scrollTop, behavior: 'smooth' });
          }, 50);
        }
      }, 80);
    }
  }

  function handleButtonClick() {
    if (isDrawerAction && hasDrawer) {
      toggleDrawer();
    } else if (block.cta) {
      handleCtaNavigation(block.cta, onCtaClick);
    }
  }

  function nextSlide() {
    if (currentSlide < totalSlides - 1) {
      slideDirection = 1;
      currentSlide++;
    }
  }

  function prevSlide() {
    if (currentSlide > 0) {
      slideDirection = -1;
      currentSlide--;
    }
  }

  function goToSlide(index: number) {
    slideDirection = index > currentSlide ? 1 : -1;
    currentSlide = index;
  }

  function handleTouchStart(e: TouchEvent) {
    touchStartX = e.touches[0].clientX;
  }

  function handleTouchEnd(e: TouchEvent) {
    touchEndX = e.changedTouches[0].clientX;
    const diff = touchStartX - touchEndX;
    if (Math.abs(diff) > 50) {
      if (diff > 0) nextSlide();
      else prevSlide();
    }
  }
</script>

{#if layout === 'media-top' || layout === 'media-bottom'}
  <!-- Vertical layouts -->
  <div
    bind:this={containerEl}
    class="feature-vertical"
    class:media-bottom={layout === 'media-bottom'}
    class:clickable={isClickable}
    class:drawer-open={drawerOpen}
    style:--text-color={textColor}
    style:--text-dark-color={textDarkColor}
    use:responsiveStyle={responsiveCSS || {}}
    use:navigate={isClickable ? { cta: block.cta, onCtaClick } : {}}
  >
    <!-- Text wrapper with grid animation -->
    <div class="text-wrapper" class:collapsed={drawerOpen}>
      <div class="text-inner">
        <div class="feature-text-content" class:center={block.alignment === 'center'} class:right={block.alignment === 'right'}>
          {#if block.title}
            <h2 class="feature-title">{block.title}</h2>
          {/if}
          {#if block.subtitle}
            <p class="feature-subtitle">{block.subtitle}</p>
          {/if}
          {#if block.content}
            {#if block.markdown}
              <Markdown content={block.content} align={block.alignment || 'left'} inline />
            {:else}
              <Text align={block.alignment || 'left'} inline>{block.content}</Text>
            {/if}
          {/if}
          {#if block.cta}
            <div class="cta-container">
              <Button
                label={block.cta.label}
                variant={block.cta.variant || 'primary'}
                size="lg"
                inline={block.alignment === 'left' || block.alignment === 'right'}
                on:click={handleButtonClick}
              />
            </div>
          {/if}
        </div>
      </div>
    </div>

    {#if hasMedia}
      <div class="feature-media">
        <Media media={block.media} playing={hasDrawer ? drawerOpen : undefined} />
      </div>
    {/if}

    <!-- Slider wrapper with grid animation -->
    {#if hasDrawer}
      <div class="slider-wrapper" class:expanded={drawerOpen}>
        <div class="slider-inner">
          <div
            class="feature-slider"
            on:touchstart={handleTouchStart}
            on:touchend={handleTouchEnd}
            role="region"
            aria-label="Content slider"
          >
            {#if contentVisible}
              <button class="slider-close" on:click|stopPropagation={toggleDrawer} aria-label="Close">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            {/if}

            <div class="slider-content" class:visible={contentVisible}>
              {#key currentSlide}
                <div
                  class="slide"
                  use:slide={{
                    translate: slideDirection * 3,
                    duration: 300,
                    delay: 50,
                    startOpacity: 0,
                    direction: slideDirection > 0 ? 'right' : 'left'
                  }}
                >
                  <Markdown content={slides[currentSlide]} align="left" inline />
                </div>
              {/key}
            </div>

            {#if totalSlides > 1}
              <div class="slider-dots" class:visible={contentVisible}>
                {#each slides as _, i}
                  <button
                    class="dot"
                    class:active={i === currentSlide}
                    on:click|stopPropagation={() => goToSlide(i)}
                    aria-label="Go to slide {i + 1}"
                  ></button>
                {/each}
              </div>
            {/if}
          </div>
        </div>
      </div>
    {/if}
  </div>
{:else}
  <!-- Horizontal layouts (media-left, media-right) -->
  <div
    bind:this={containerEl}
    class="feature-horizontal"
    class:media-left={layout === 'media-left'}
    class:media-right={layout === 'media-right'}
    class:clickable={isClickable}
    class:drawer-open={drawerOpen}
    style:--text-color={textColor}
    style:--text-dark-color={textDarkColor}
    style:--layout-drawer-off="{drawerOffWidth}px"
    style:--layout-drawer-on="{drawerOnWidth}px"
    use:responsiveStyle={responsiveCSS || {}}
    use:navigate={isClickable ? { cta: block.cta, onCtaClick } : {}}
  >
    <!-- Text wrapper with grid animation -->
    <div class="text-wrapper" class:collapsed={drawerOpen}>
      <div class="text-inner">
        <div class="feature-text-content" class:center={block.alignment === 'center'} class:right={block.alignment === 'right'} class:hidden={!textVisible}>
          {#if block.title}
            <h2 class="feature-title">{block.title}</h2>
          {/if}
          {#if block.subtitle}
            <p class="feature-subtitle">{block.subtitle}</p>
          {/if}
          {#if block.content}
            {#if block.markdown}
              <Markdown content={block.content} align={block.alignment || 'left'} inline />
            {:else}
              <Text align={block.alignment || 'left'} inline>{block.content}</Text>
            {/if}
          {/if}
          {#if block.cta}
            <div class="cta-container">
              <Button
                label={block.cta.label}
                variant={block.cta.variant || 'primary'}
                size="lg"
                inline={block.alignment === 'left' || block.alignment === 'right'}
                on:click={handleButtonClick}
              />
            </div>
          {/if}
        </div>
      </div>
    </div>

    <!-- Media - always visible -->
    {#if hasMedia}
      <div class="feature-media" class:drawer-active={drawerOpen}>
        <Media media={block.media} playing={hasDrawer ? drawerOpen : undefined} />
      </div>
    {/if}

    <!-- Slider wrapper with grid animation -->
    {#if hasDrawer}
      <div class="slider-wrapper" class:expanded={drawerOpen}>
        <div class="slider-inner">
          <div
            class="feature-slider"
            on:touchstart={handleTouchStart}
            on:touchend={handleTouchEnd}
            role="region"
            aria-label="Content slider"
          >
            {#if contentVisible}
              <button class="slider-close" on:click|stopPropagation={toggleDrawer} aria-label="Close">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            {/if}

            {#if totalSlides > 1 && contentVisible}
              <button
                class="slider-nav slider-prev"
                class:disabled={currentSlide === 0}
                on:click|stopPropagation={prevSlide}
                aria-label="Previous slide"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
              <button
                class="slider-nav slider-next"
                class:disabled={currentSlide === totalSlides - 1}
                on:click|stopPropagation={nextSlide}
                aria-label="Next slide"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
            {/if}

            <div class="slider-content" class:visible={contentVisible}>
              {#key currentSlide}
                <div
                  class="slide"
                  use:slide={{
                    translate: slideDirection * 3,
                    duration: 300,
                    delay: 50,
                    startOpacity: 0,
                    direction: slideDirection > 0 ? 'right' : 'left'
                  }}
                >
                  <Markdown content={slides[currentSlide]} align="left" inline />
                </div>
              {/key}
            </div>

            {#if totalSlides > 1}
              <div class="slider-dots" class:visible={contentVisible}>
                {#each slides as _, i}
                  <button
                    class="dot"
                    class:active={i === currentSlide}
                    on:click|stopPropagation={() => goToSlide(i)}
                    aria-label="Go to slide {i + 1}"
                  ></button>
                {/each}
              </div>
            {/if}
          </div>
        </div>
      </div>
    {/if}
  </div>
{/if}

<style lang="postcss">
  /* Base colors */
  .feature-vertical,
  .feature-horizontal {
    color: var(--text-color, inherit);
  }

  :global([data-theme='dark']) .feature-vertical,
  :global([data-theme='dark']) .feature-horizontal {
    color: var(--text-dark-color, inherit);
  }

  /* Grid animation wrappers - Mobile: height animation */
  .text-wrapper {
    display: grid;
    grid-template-rows: 1fr;
    transition: grid-template-rows 200ms ease-out;
  }

  .text-wrapper.collapsed {
    grid-template-rows: 0fr;
  }

  .text-wrapper > .text-inner {
    overflow: hidden;
    min-height: 0;
  }

  .slider-wrapper {
    display: grid;
    grid-template-rows: 0fr;
    transition: grid-template-rows 200ms ease-out;
  }

  .slider-wrapper.expanded {
    grid-template-rows: 1fr;
  }

  .slider-wrapper > .slider-inner {
    overflow: hidden;
    min-height: 0;
  }

  /* Vertical layout */
  .feature-vertical {
    display: flex;
    flex-direction: column;
    gap: 0;
    max-width: 1200px;
    margin: 0 auto;
  }

  .feature-vertical .text-wrapper {
    order: 1;
  }

  .feature-vertical .feature-media {
    order: 2;
  }

  .feature-vertical .slider-wrapper {
    order: 3;
  }

  .feature-vertical.media-bottom .text-wrapper {
    order: 2;
  }

  .feature-vertical.media-bottom .feature-media {
    order: 1;
  }

  /* Horizontal layout - Mobile first */
  .feature-horizontal {
    container-type: inline-size;
    display: flex;
    flex-direction: column;
    gap: 0;
    margin: 0 auto;
    max-width: 100%;
    width: var(--layout-drawer-off, 800px);
    transition: width 400ms cubic-bezier(0.4, 0, 0.2, 1);
  }

  .feature-horizontal.drawer-open {
    width: var(--layout-drawer-on, 1096px);
  }

  .feature-horizontal .text-wrapper {
    order: 1;
  }

  .feature-horizontal .feature-media {
    order: 2;
  }

  .feature-horizontal .slider-wrapper {
    order: 3;
  }

  /* Media */
  .feature-media {
    width: 100%;
    line-height: 0;
  }

  .feature-media :global(.media-video) {
    display: block;
    background: #000;
  }

  /* Text content */
  .feature-text-content {
    padding: 1.5rem 1rem;
    max-width: 600px;
    margin: 0 auto;
    width: 100%;
    box-sizing: border-box;
    opacity: 1;
    transition: opacity 150ms ease-out;
  }

  .feature-text-content.hidden {
    opacity: 0;
  }

  .feature-title {
    font-size: 1.8rem;
    font-weight: 700;
    line-height: 1.15;
    margin: 0 0 1rem 0;
    color: currentColor;
  }

  .feature-subtitle {
    font-size: 1.25rem;
    font-weight: 400;
    line-height: 1.25;
    margin: 0 0 1rem 0;
    color: currentColor;
    opacity: 0.8;
  }

  .cta-container {
    margin-top: 1.5rem;
  }

  .feature-text-content.center {
    text-align: center;
  }

  .feature-text-content.right {
    text-align: right;
  }

  .feature-text-content.center .cta-container {
    display: flex;
    justify-content: center;
  }

  /* Slider - Mobile */
  .feature-slider {
    display: flex;
    flex-direction: column;
    position: relative;
    min-height: 200px;
    max-height: 40vh;
    padding: 2.5rem 1rem 1rem;
    overflow: hidden;
  }

  .slider-close {
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
    z-index: 10;
    background: rgba(0, 0, 0, 0.4);
    border: none;
    border-radius: 50%;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: white;
    transition: background 200ms ease, transform 200ms ease;
  }

  .slider-close:hover {
    background: rgba(0, 0, 0, 0.6);
    transform: scale(1.1);
  }

  /* Nav buttons - hidden on mobile */
  .slider-nav {
    display: none;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 10;
    background: rgba(0, 0, 0, 0.4);
    border: none;
    border-radius: 50%;
    width: 36px;
    height: 36px;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: white;
    transition: background 200ms ease, opacity 200ms ease;
  }

  .slider-nav:hover {
    background: rgba(0, 0, 0, 0.6);
  }

  .slider-nav.disabled {
    opacity: 0.3;
    cursor: default;
  }

  .slider-prev {
    left: 0.5rem;
  }

  .slider-next {
    right: 0.5rem;
  }

  /* Slider content */
  .slider-content {
    flex: 1;
    position: relative;
    overflow: hidden;
    display: flex;
    align-items: center;
    opacity: 0;
    transition: opacity 200ms ease-out;
  }

  .slider-content.visible {
    opacity: 1;
  }

  .slide {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    padding: 0 0.5rem;
    overflow-y: auto;
  }

  .slide > :global(*) {
    width: 100%;
  }

  .slide :global(h2),
  .slide :global(h3) {
    margin-top: 0;
    margin-bottom: 0.75rem;
  }

  .slide :global(p) {
    margin-bottom: 0.75rem;
  }

  .slide :global(li) {
    margin-bottom: 0.5rem;
  }

  /* Dots */
  .slider-dots {
    display: flex;
    justify-content: center;
    gap: 0.5rem;
    padding-top: 1rem;
    flex-shrink: 0;
    opacity: 0;
    transition: opacity 200ms ease-out;
  }

  .slider-dots.visible {
    opacity: 1;
  }

  .dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    border: none;
    background: rgba(255, 255, 255, 0.4);
    cursor: pointer;
    padding: 0;
    transition: background 200ms ease, transform 200ms ease;
  }

  .dot:hover {
    background: rgba(255, 255, 255, 0.6);
  }

  .dot.active {
    background: white;
    transform: scale(1.25);
  }

  /* Clickable styling */
  .feature-vertical.clickable,
  .feature-horizontal.clickable {
    cursor: pointer;
    transition: transform 0.2s ease, opacity 0.2s ease;
  }

  .feature-vertical.clickable:hover,
  .feature-horizontal.clickable:hover {
    transform: scale(1.02);
    opacity: 0.95;
  }

  /* Desktop styles */
  @container (min-width: 768px) {
    .feature-horizontal {
      flex-direction: row;
      align-items: stretch;
    }

    /* Desktop: text wrapper with width animation */
    .feature-horizontal .text-wrapper {
      order: 2;
      flex: 0 0 50%;
      display: flex;
      overflow: hidden;
      transition: flex 200ms ease-out;
    }

    .feature-horizontal .text-wrapper.collapsed {
      flex: 0 0 0px;
      display: none;
    }

    .feature-horizontal .text-wrapper > .text-inner {
      display: flex;
      flex-direction: column;
      justify-content: center;
      flex: 1;
    }

    .feature-horizontal .feature-text-content {
      padding: 1rem;
      max-width: none;
    }

    .feature-horizontal .feature-media {
      order: 1;
      flex: 0 0 50%;
      transition: none;
    }

    /* Desktop: slider wrapper with width animation */
    .feature-horizontal .slider-wrapper {
      order: 2;
      flex: 0 0 0px;
      display: flex;
      overflow: hidden;
      transition: flex 250ms cubic-bezier(0.4, 0, 0.2, 1);
      position: relative;
    }

    .feature-horizontal .slider-wrapper.expanded {
      flex: 0 0 50%;
    }

    .feature-horizontal .slider-wrapper > .slider-inner {
      display: flex;
      flex: 1;
      min-width: 300px;
      position: absolute;
      inset: 0;
    }

    .feature-horizontal .feature-slider {
      flex: 1;
      max-height: none;
      min-height: 0;
      padding: 1.5rem 3rem;
      box-sizing: border-box;
    }

    /* media-right: media on right */
    .feature-horizontal.media-right .text-wrapper {
      order: 1;
    }

    .feature-horizontal.media-right .feature-media {
      order: 2;
    }

    .feature-horizontal.media-right .slider-wrapper {
      order: 1;
    }

    /* Show nav buttons on desktop */
    .slider-nav {
      display: flex;
    }

    .slide {
      padding: 0 2.5rem;
    }

    /* Desktop: allow full width and ensure centering during transition */
    .feature-horizontal {
      max-width: 100vw;
      position: relative;
      left: 50%;
      transform: translateX(-50%);
    }

    .feature-horizontal .feature-media.drawer-active {
      flex: 0 0 50%;
      align-self: stretch;
      display: flex;
      flex-direction: column;
    }

    .feature-horizontal .feature-media.drawer-active :global(.media-video) {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 0;
    }

    .feature-horizontal .feature-media.drawer-active :global(video) {
      max-width: 100%;
      max-height: 100%;
      width: 100%;
      height: 100%;
      object-fit: contain;
    }

    .feature-horizontal.drawer-open .slider-wrapper {
      align-self: stretch;
    }

    .feature-horizontal.drawer-open .slider-wrapper.expanded {
      flex: 0 0 50%;
    }
  }
</style>
